import { useCallback, useState } from "react";
import { METRICS_EVENTS, AutomaticStreakCguStatusResponse } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useSignAutomaticStreakCgu } from "./useSignAutomaticStreakCgu";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useAutomaticStreakCguStore } from "@/store/automaticStreakCguStore";
import { updateAutomaticStreakCguAcknowledgment, getAutomaticStreakCguStatus } from "@/utils/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

export const useAutomaticStreakCgu = () => {
  const {
    riskAcknowledged,
    automatedTradingAcknowledged,
    termsAndConditionsAccepted,
    setRiskAcknowledged,
    setAutomatedTradingAcknowledged,
    setTermsAndConditionsAccepted,
    reset,
  } = useAutomaticStreakCguStore();

  const canProceed = riskAcknowledged && automatedTradingAcknowledged && termsAndConditionsAccepted;
  const { auth, authenticated } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { enqueueSnackbar } = useSnackbar();
  const { signCguAcceptance, isSigning } = useSignAutomaticStreakCgu();
  const [isSubmittingToBackend, setIsSubmittingToBackend] = useState(false);

  // Query CGU status from dedicated endpoint
  const { data: cguStatusData, isLoading: isCguStatusLoading } = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK_CGU_STATUS],
    queryFn: async (): Promise<boolean> => {
      const result = await getAutomaticStreakCguStatus(auth);
      if (isServerApiError(result) || isServerApiRfcError(result)) {
        return false;
      }
      return (result as AutomaticStreakCguStatusResponse).acknowledged;
    },
    enabled: authenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check if user has already acknowledged CGU on the backend
  const isAlreadyAcknowledged = cguStatusData ?? false;

  // Mutation to update acknowledgment on backend with signature
  const { mutateAsync: updateAcknowledgmentBackend, isPending: isMutating } = useMutation({
    mutationFn: async ({ signature, nonce }: { signature: string; nonce: number }) => {
      return updateAutomaticStreakCguAcknowledgment(auth, { signature, nonce });
    },
    onSuccess: () => {
      // Invalidate CGU status query to refetch with updated acknowledgment
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK_CGU_STATUS] });
      // Reset local store state after successful backend update
      reset();
    },
    onError: () => {
      enqueueSnackbar("Failed to save automatic streak terms acknowledgment. Please try again.", {
        variant: "error",
      });
    },
  });

  // Combined submitting state (signing + backend call)
  const isSubmitting = isSigning || isMutating || isSubmittingToBackend;

  // Handle confirm button click - signs message first, then sends to backend
  const handleConfirm = useCallback(async () => {
    if (!canProceed) return false;

    try {
      setIsSubmittingToBackend(true);

      // Step 1: Sign the CGU message with the user's wallet
      const { signature, nonce } = await signCguAcceptance();

      // Step 2: Send signature to backend for verification and storage
      await updateAcknowledgmentBackend({ signature, nonce });

      // Track when user signs the CGU
      trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CGU_SIGNED, {});

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Automatic streak CGU acknowledgment error:", error);

      // User rejected signature
      if (error instanceof Error && error.message.includes("User rejected")) {
        enqueueSnackbar("Signature rejected. Please sign the message to accept the terms.", {
          variant: "warning",
        });
      } else {
        // Backend or other error - show generic message
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save acknowledgment. Please try again.";
        enqueueSnackbar(errorMessage, {
          variant: "error",
        });
      }
      return false;
    } finally {
      setIsSubmittingToBackend(false);
    }
  }, [canProceed, signCguAcceptance, updateAcknowledgmentBackend, trackEvent, enqueueSnackbar]);

  return {
    riskAcknowledged,
    automatedTradingAcknowledged,
    termsAndConditionsAccepted,
    canProceed,
    isAlreadyAcknowledged,
    isCguStatusLoading,
    isSubmitting,
    setRiskAcknowledged,
    setAutomatedTradingAcknowledged,
    setTermsAndConditionsAccepted,
    handleConfirm,
    reset,
  };
};
